import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  FileCheck, 
  FileText, 
  Calculator, 
  Percent, 
  Check, 
  ArrowRight, 
  ShieldCheck, 
  Building2, 
  BadgePercent, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  Clock,
  Layers,
  Scale,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'
import ContactForm from '../components/ContactForm'

import RelatedServices from '../components/RelatedServices'
import FAQSection from '../components/FAQSection'
import { updateMeta } from '../lib/utils'

export default function AnnualReportPage() {
  useEffect(() => {
    updateMeta(
      "Årsregnskab & Årsrapport | Fast pris",
      "Korrekt udarbejdelse og rettidig indberetning af årsrapport og årsregnskab til Erhvervsstyrelsen. Fast pris for selskaber og selvstændige."
    )
  }, [])

  const tasksList = [
    {
      title: "Gennemgang af årets bogføring og bilag",
      desc: "Grundig gennemgang af årets posteringer, så bilagsfejl og uafklarede poster findes og rettes.",
      icon: FileText
    },
    {
      title: "Afstemning af banken og kontrol af moms",
      desc: "Sikrer fuld overensstemmelse mellem bank, bogføring og de indberettede momsterminer.",
      icon: Calculator
    },
    {
      title: "Opstilling af resultatopgørelse, balance og relevante noter",
      desc: "Korrekt regnskabsopstilling efter årsregnskabsloven med balance og nødvendige specifikationer.",
      icon: Scale
    },
    {
      title: "Opgørelse af skattepligtig indkomst og relevante skatteoplysninger",
      desc: "Korrekt skatteberegning og indberetning til Erhvervsstyrelsen eller Skattestyrelsen (oplysningsskema).",
      icon: FileCheck
    }
  ]

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      
      {/* Hero Sektion */}
      <section className="bg-white border-b border-stone-200 py-16 lg:py-24 relative overflow-hidden">
        {/* Subtile dekorative baggrunds-accenter */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-orange-100/50 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 -mb-20 w-80 h-80 rounded-full bg-stone-100/60 blur-2xl pointer-events-none" />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            {/* Top Tag */}
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-100 text-stone-700 text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6 border border-stone-200">
              <span className="w-2 h-2 rounded-full bg-orange-600"></span>
              Thellefsen Regnskab &bull; Årsafslutning & Årsrapport i hele Danmark
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight leading-tight">
              Årsrapport og årsregnskab med styr på årets tal
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-stone-600 leading-relaxed">
              Skal du have afsluttet regnskabsåret, men mangler tid eller overblik over tallene? Hos{' '}
              <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
                Thellefsen Regnskab
              </Link>{' '}
              hjælper vi med årsrapport, årsregnskab og årsafslutning for selskaber og enkeltmandsvirksomheder. Du får hjælp til de konkrete opgaver og en forklaring af tallene, så du ved, hvad årets regnskab viser.
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <a
                href="#kontakt-form-section"
                className="inline-flex items-center justify-center rounded-none bg-orange-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a
                href="#virksomhedsform"
                className="inline-flex items-center justify-center rounded-none bg-stone-100 border border-stone-300 px-6 py-3.5 text-sm font-semibold text-stone-800 hover:bg-stone-200 transition-colors"
              >
                Krav for ApS & enkeltmandsvirksomhed
              </a>
            </div>

            {/* Hurtige fordele */}
            <div className="mt-12 pt-8 border-t border-stone-200 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Rettidig indberetning</div>
                  <div className="text-xs text-stone-500">Overhold alle frister</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Korrekt skat</div>
                  <div className="text-xs text-stone-500">Undgå ubehagelige overraskelser</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <FileCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Direkte kontakt</div>
                  <div className="text-xs text-stone-500">Peter forklarer tallene</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 1: Få hjælp til årsafslutning, før opgaverne hober sig op */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
              Tryg årsafslutning
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight mt-2">
              Få hjælp til årsafslutning, før opgaverne hober sig op
            </h2>

            <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg mt-6">
              <p>
                Manglende bilag og forskelle mellem banken og bogføringen kan gøre årets afslutning mere omfattende end ventet. Vi gennemgår årets bogføring og afstemninger, så eventuelle mangler bliver opdaget og kan afklares. Det giver et bedre grundlag for at afslutte regnskabet og udarbejde de nødvendige opgørelser.
              </p>
              <p>
                Du kan få hjælp til årsafslutningen som en særskilt opgave eller som en del af et løbende samarbejde. Vi aftaler, hvilke opgaver vi overtager, og hvad du selv står for. Afhængigt af din virksomheds behov kan arbejdet blandt andet omfatte følgende opgaver.
              </p>
            </div>
          </div>

          {/* 4 Opgaver Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {tasksList.map((item, index) => {
              const IconComp = item.icon
              return (
                <div 
                  key={index}
                  className="bg-white border border-stone-200 p-6 sm:p-8 shadow-sm hover:border-orange-500/50 hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    <div className="h-12 w-12 bg-stone-50 border border-stone-200 flex items-center justify-center text-orange-600 mb-6">
                      <IconComp className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-stone-900 leading-snug mb-3">
                      {item.title}
                    </h3>
                    <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-100 flex items-center gap-2 text-xs font-semibold text-stone-500">
                    <Check className="h-4 w-4 text-orange-600" />
                    <span>Inkluderet efter aftale</span>
                  </div>
                </div>
              )
            })}
          </div>

        </div>
      </section>

      {/* Sektion 2 & 3: Årsrapport eller årsregnskab & To-delt virksomhedsform */}
      <section id="virksomhedsform" className="py-16 lg:py-24 bg-white border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
              Klar forskel
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight mt-2">
              Årsrapport eller årsregnskab, hvad har din virksomhed brug for?
            </h2>
            <p className="mt-4 text-stone-600 text-base sm:text-lg leading-relaxed">
              Årsregnskabet viser årets resultat og virksomhedens økonomiske stilling ved årets slutning. Årsrapporten er det samlede dokument, som årsregnskabet indgår i sammen med andre krævede oplysninger. Kravene afhænger blandt andet af virksomhedens form og størrelse. Derfor tager vi udgangspunkt i netop din virksomhed.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            
            {/* Kort 1: Årsrapport for dit anpartsselskab (ApS) */}
            <div className="bg-stone-50 border border-stone-200 p-8 sm:p-10 flex flex-col justify-between relative shadow-sm">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-stone-200 text-xs font-bold text-orange-700 uppercase tracking-wide mb-6">
                  <Building2 className="h-3.5 w-3.5" />
                  Selskaber &bull; ApS / A/S
                </div>

                <h3 className="text-2xl font-bold text-stone-900 tracking-tight mb-4">
                  Årsrapport for dit anpartsselskab
                </h3>

                <div className="space-y-4 text-stone-600 leading-relaxed text-base">
                  <p>
                    For et anpartsselskab omfatter hjælpen opstilling af årsrapporten og indberetning til Erhvervsstyrelsen. Vi gennemgår årets bogføring og opstiller resultatopgørelse, balance og relevante noter. Er selskabet omfattet af revisionspligt, skal revisionen varetages af en godkendt revisor.
                  </p>
                  <p>
                    Et anpartsselskab skal som udgangspunkt indsende årsrapporten senest seks måneder efter regnskabsårets afslutning. Følger regnskabet kalenderåret, er fristen normalt den 30. juni. Det er derfor en fordel at få afklaret manglende bilag og åbne spørgsmål i god tid frem for at vente til fristen nærmer sig.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200 bg-white p-5 border border-stone-200">
                <div className="flex items-center gap-3 text-sm text-stone-800 font-semibold mb-2">
                  <Calendar className="h-4 w-4 text-orange-600" />
                  <span>Vigtig frist: Senest 30. juni</span>
                </div>
                <p className="text-xs text-stone-500 leading-relaxed">
                  Undgå påkravsgebyrer og tvangsopløsning ved at få startet årsafslutningen i god tid sammen med Peter.
                </p>
              </div>
            </div>

            {/* Kort 2: Årsregnskab for din enkeltmandsvirksomhed */}
            <div className="bg-stone-50 border border-stone-200 p-8 sm:p-10 flex flex-col justify-between relative shadow-sm">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-stone-200 text-xs font-bold text-orange-700 uppercase tracking-wide mb-6">
                  <Layers className="h-3.5 w-3.5" />
                  Selvstændige &bull; Enkeltmandsvirksomhed
                </div>

                <h3 className="text-2xl font-bold text-stone-900 tracking-tight mb-4">
                  Årsregnskab for din enkeltmandsvirksomhed
                </h3>

                <div className="space-y-4 text-stone-600 leading-relaxed text-base">
                  <p>
                    En enkeltmandsvirksomhed skal som udgangspunkt ikke indsende en årsrapport til Erhvervsstyrelsen. Til gengæld skal årets økonomi opgøres som grundlag for de skatteoplysninger, du indberetter. Derfor er et gennemarbejdet årsregnskab også relevant, selv om du ikke driver et selskab.
                  </p>
                  <p>
                    Vi hjælper med årsafslutning, opgørelse af virksomhedens skattepligtige indkomst og indberetning af relevante oplysninger i dit oplysningsskema. Samtidig gennemgår vi tallene med dig, så du kan se, hvad virksomheden har tjent, og hvordan indtægter og udgifter påvirker årets resultat.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200 bg-white p-5 border border-stone-200">
                <div className="flex items-center gap-3 text-sm text-stone-800 font-semibold mb-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-600" />
                  <span>Korrekt oplysningsskema til SKAT</span>
                </div>
                <p className="text-xs text-stone-500 leading-relaxed">
                  Få fuld kontrol over dit overskud, personlige fradrag og eventuelle skatteoptimeringer.
                </p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Sektion 4: Klargøring til årsregnskab med en klar arbejdsdeling */}
      <section className="py-16 lg:py-24 bg-stone-50 border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="bg-white border border-stone-200 p-8 sm:p-12 shadow-sm">
            <div className="max-w-3xl">
              <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                Samarbejde & overblik
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-2 mb-6">
                Klargøring til årsregnskab med en klar arbejdsdeling
              </h2>

              <div className="space-y-4 text-stone-600 text-base sm:text-lg leading-relaxed">
                <p>
                  Klargøring til årsregnskab handler om at få bogføringen og dokumentationen på plads, inden regnskabet stilles op. Vi afklarer opgavens omfang og gennemgår det materiale, der er til rådighed. Har du allerede en samarbejdspartner til den endelige opstilling, kan vi hjælpe med at gøre grundlaget klar.
                </p>
                <p>
                  En god årsafslutning begynder ofte med de løbende rutiner. Når bilag bliver bogført, og banken bliver afstemt undervejs, er der færre løse ender ved årets slutning. Har du brug for{' '}
                  <Link 
                    to="/bogfoering" 
                    className="font-semibold text-orange-600 hover:text-orange-700 underline decoration-orange-300 underline-offset-4 transition-colors"
                  >
                    hjælp til bogføring
                  </Link>
                  , kan vi også tage os af den del og skabe sammenhæng mellem årets posteringer og regnskabet.
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200 flex flex-wrap gap-4">
                <Link
                  to="/bogfoering"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4"
                >
                  Læs mere om vores løbende bogføring
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>



        </div>
      </section>

      {/* Sektion 5: Hvad koster hjælp til årsregnskab? */}
      <section className="py-16 lg:py-20 bg-white border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center p-3 bg-stone-100 border border-stone-200 mb-6 text-orange-600">
              <BadgePercent className="h-8 w-8" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight">
              Hvad koster hjælp til årsregnskab?
            </h2>
            <p className="mt-6 text-stone-600 text-base sm:text-lg leading-relaxed">
              Prisen afhænger blandt andet af virksomhedens form, omfanget af bogføringen og behovet for afstemning eller oprydning. Vi tilbyder et uforpligtende tilbud på opgaven, så du kan tage stilling til både pris og indhold. Det gør det tydeligt, hvad hjælpen omfatter, inden vi går i gang med dit regnskab.
            </p>

            <div className="mt-8 p-6 sm:p-8 bg-stone-50 border border-stone-200 text-left">
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Ønsker du at samle opgaverne til en fast årlig pris?
              </h3>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed mb-4">
                Ønsker du at samle opgaverne, tilbyder vi en samlet regnskabsløsning til en fast årlig pris. Den omfatter bogføring, bankafstemning, momsindberetning, årsregnskab og relevante skatteindberetninger. Adgang til regnskabsprogram indgår også, og vi aftaler indholdet ud fra din virksomheds behov.
              </p>
              <Link
                to="/regnskabsservice"
                className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4"
              >
                Læs mere om vores samlede regnskabspakke
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-8">
              <a
                href="#kontakt-form-section"
                className="inline-flex items-center justify-center rounded-none bg-orange-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Indhent et uforpligtende tilbud her
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: FAQ */}
      <FAQSection
        title="Ofte stillede spørgsmål om årsrapport og årsregnskab"
        subtitle="Få svar på de mest almindelige spørgsmål om årsafslutning, frister, selskabsskat og revision."
        items={[
          {
            question: "Hvad er forskellen på et årsregnskab og en årsrapport?",
            answer: "Et årsregnskab for en enkeltmandsvirksomhed er en skattemæssig opgørelse med resultat og balance, som danner grundlag for din personlige selvangivelse til SKAT. En årsrapport for et selskab (ApS) skal derimod opstilles efter årsregnskabslovens formelle regler og indsendes offentligt til Erhvervsstyrelsen sammen med ledelsespåtegning og noter."
          },
          {
            question: "Hvornår er tidsfristen for indberetning af årsrapporten?",
            answer: "For selskaber (ApS, A/S) skal årsrapporten være godkendt på generalforsamlingen og modtaget hos Erhvervsstyrelsen senest 6 måneder efter regnskabsårets udløb. For enkeltmandsvirksomheder skal det skattemæssige oplysningsskema være indberettet senest den 1. juli året efter, og vi sørger for, at dine tal er klar i god tid før begge frister."
          },
          {
            question: "Kan mit selskab fravælge revision, og hjælper I med erklæringen?",
            answer: "Ja, langt de fleste mindre selskaber opfylder betingelserne for at fravælge revisionspligten og spare udgiften til en statsautoriseret revisor. Vi hjælper med at vurdere dine nøgletal, udforme ledelseserklæringen korrekt og sikre, at fravalget registreres formelt og lovligt hos Erhvervsstyrelsen i forbindelse med årsrapporten."
          },
          {
            question: "Hvordan beregnes selskabsskat og skattemæssige afskrivninger?",
            answer: "Vi foretager en grundig skatteberegning, hvor vi skelner mellem regnskabsmæssige og skattemæssige værdier på driftsmidler og inventar. Vi sørger for optimale afskrivninger og udnyttelse af fremførte underskud, så du betaler præcis den korrekte selskabsskat (22 %) og undgår restskattetillæg hos Skattestyrelsen."
          },
          {
            question: "Hvad skal jeg indsende til jer forud for udarbejdelsen af årsregnskabet?",
            answer: "Vi skal primært bruge årets bogføring, kontoudskrifter pr. 31/12, eventuelle låneopgørelser, varelagerliste og dokumentation for større anlægsaktiver eller restgæld. Hvis vi allerede står for din løbende bogføring, har vi automatisk det meste liggende klar og behøver blot enkelte statusoplysninger fra dig før afslutningen."
          }
        ]}
      />

      {/* Sektion 7: Relaterede ydelser */}
      <RelatedServices currentPath="/aarsrapport-og-aarsregnskab" title="Se også vores andre ydelser" />

      {/* Sektion 8: Tal med Peter om din årsrapport & Kontakt */}
      <section id="kontakt-form-section" className="py-16 lg:py-24 bg-stone-50 border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Tekst og info */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                  Direkte kontakt
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-2">
                  Tal med Peter om din årsrapport
                </h2>

                <div className="space-y-4 text-stone-600 text-base sm:text-lg leading-relaxed mt-4">
                  <p>
                    Du har direkte kontakt til Peter, som også står for opgaven. Vi har base i Espergærde og hjælper virksomheder i Nordsjælland, på Sjælland og i resten af Danmark. Kontakt os for en uforpligtende samtale om din årsrapport eller dit årsregnskab, så vi kan afklare, hvilken hjælp du har brug for.
                  </p>
                </div>

                {/* Profilkort Peter */}
                <div className="mt-8 bg-white border border-stone-200 p-6 shadow-sm">
                  <div className="flex items-center gap-4 mb-4">
                    <img 
                      src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/img-4119.jpg" 
                      alt="Peter Thellefsen - Revisor og indehaver af Thellefsen Regnskab ApS" 
                      className="w-16 h-16 object-cover border border-stone-200 shrink-0"
                    />
                    <div>
                      <div className="font-bold text-stone-900 text-base">Peter Thellefsen</div>
                      <div className="text-xs text-stone-500">Revisor & Indehaver</div>
                      <div className="text-xs text-orange-600 font-medium mt-0.5">Thellefsen Regnskab ApS</div>
                    </div>
                  </div>

                  <div className="space-y-2.5 pt-3 border-t border-stone-100 text-xs sm:text-sm text-stone-600">
                    <div className="flex items-center gap-2.5">
                      <Phone className="h-4 w-4 text-orange-600 shrink-0" />
                      <a href="tel:+4531335470" className="hover:text-orange-600 transition-colors font-medium">
                        31 33 54 70
                      </a>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Mail className="h-4 w-4 text-orange-600 shrink-0" />
                      <a href="mailto:info@tregnskab.dk" className="hover:text-orange-600 transition-colors">
                        info@tregnskab.dk
                      </a>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <MapPin className="h-4 w-4 text-orange-600 shrink-0" />
                      <span>Strandvangsvej 9, 3060 Espergærde</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Kontaktformular */}
            <div className="lg:col-span-7">
              <ContactForm 
                defaultService="aarsregnskab" 
                title="Få et uforpligtende tilbud på din årsafslutning" 
                subtitle="Fortæl kort om din virksomhed og virksomhedsform, så vender Peter hurtigt tilbage." 
              />
            </div>

          </div>

        </div>
      </section>

    </div>
  )
}
